/**
 * @file LinkedDocuments.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-02
 * @modified 2026-11-03
 * @description "Linked documents" — one panel for the customer order, invoice,
 *              purchase order and bill modals. Every document linked to the
 *              record (its order, invoices, credit notes allocated, POs incl.
 *              the PO-H, bills) as a chip that opens that document's own modal
 *              on top of this one.
 *
 *              <LinkedDocuments type="invoice" id={invoice.id} refreshKey={invoice} onChange={reload} />
 *
 *              `id` is always the LOCAL id — for a PO, purchase_orders.id. The
 *              server returns each chip's `openId`, which for a PO is the Xero
 *              id PurchaseOrderModal looks it up by, so no caller has to know
 *              that. Chips open through EntityModalProvider (openEntity), the
 *              one place that knows each modal's props.
 *
 *              Renders nothing until something is linked: no heading over an
 *              empty box, no spinner for what is usually a few chips.
 *
 *              Rep scope and cost visibility are the server's
 *              (services/linkedDocumentsService.js): a PO's or bill's amount
 *              comes back null for a user without costs.view and is simply not
 *              shown; a rep gets no bills at all.
 *
 * 1.1.0 (2026-10-07): third-party haulage links (utils/billHaulageLink). A
 * haulier's bill reached only as haulage reads "Haulage" (tip: "for PO-…");
 * from that bill, the PO it carried reads "Haulage for".
 *
 * 1.2.0 (2026-10-08): credits and debits raised for an invoice ("Add credit" /
 * "Add debit", invoicing.invoice_links). On the invoice: "Credit CN-…" /
 * "Debit INV-…"; on the credit or debit: "Credit for INV-…" / "Debit for
 * INV-…" (`adjustment` on the chip, from the server).
 *
 * 1.3.0 (2026-10-29, Cam — "PO modal needs to show linked PO-H when no SO-D
 * linking the two"): a stock or self-billed PO and the PO-H raised for it are
 * linked with no order between them. From the PO its PO-H is a "PO-H" chip;
 * from the PO-H the PO reads "Haulage for" (`haulageOf` from the server).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import { poStatusLabel } from '../utils/poStatus';
import { useEntityModal, ENTITY_REGISTRY } from './EntityModalProvider';
import './LinkedDocuments.css';

// "Add credit" / "Add debit" links (services/linkedDocumentsService `adjustment`).
const ADJUSTMENT_BADGE = { credit: 'Credit', debit: 'Debit', credit_for: 'Credit for', debit_for: 'Debit for' };
// A part of a booking (booked over — services/linkedDocumentsService 1.4.0).
const bookingText = (doc) => (doc.booking && doc.booking.of
  ? `Booked over — part ${doc.booking.part} of ${doc.booking.of}` : null);

const ADJUSTMENT_TIP = {
  credit: 'Credit raised for this invoice:',
  debit: 'Debit raised for this invoice:',
  credit_for: 'This credit was raised for',
  debit_for: 'This debit was raised for'
};

/** The short badge each chip leads with. */
export function typeBadge(doc) {
  if (doc.adjustment && ADJUSTMENT_BADGE[doc.adjustment]) return ADJUSTMENT_BADGE[doc.adjustment];
  if (doc.kind === 'customer_order') return 'SO';
  if (doc.kind === 'invoice') return 'INV';
  if (doc.kind === 'credit_note') {
    // Xero overpayments / prepayments are allocated like credit notes but are
    // not credit notes, and saying so would be wrong.
    if (doc.creditType === 'overpayment') return 'OP';
    if (doc.creditType === 'prepayment') return 'PP';
    return 'CN';
  }
  if (doc.kind === 'purchase_order') {
    if (doc.haulageLink || doc.haulageOf) return 'Haulage for';
    return doc.poType === 'haulage' ? 'PO-H' : 'PO';
  }
  if (doc.kind === 'bill') return doc.haulageFor?.length ? 'Haulage' : 'Bill';
  return '';
}

function kindLabel(doc) {
  if (doc.adjustment && ADJUSTMENT_TIP[doc.adjustment]) return ADJUSTMENT_TIP[doc.adjustment];
  if (doc.kind === 'credit_note' && doc.creditType === 'overpayment') return 'Overpayment';
  if (doc.kind === 'credit_note' && doc.creditType === 'prepayment') return 'Prepayment';
  if (doc.kind === 'purchase_order' && doc.poType === 'haulage') return 'Haulage PO';
  if (doc.kind === 'purchase_order' && doc.haulageLink) return 'PO this bill is the haulage for:';
  if (doc.kind === 'purchase_order' && doc.haulageOf) return 'PO this haulage order collects:';
  if (doc.kind === 'bill' && doc.haulageFor?.length) return 'Haulage bill';
  return ENTITY_REGISTRY[doc.kind]?.label || 'Document';
}

const statusText = (doc) => (doc.kind === 'purchase_order' ? poStatusLabel(doc.status) : (doc.status || ''));
const statusClass = (doc) => String(doc.status || '').toLowerCase().replace(/[^a-z]+/g, '-');

function LinkedDocuments({ type, id, refreshKey, onChange, title = 'Linked documents' }) {
  const { openEntity, available } = useEntityModal();
  const [docs, setDocs] = useState([]);

  const load = useCallback(async () => {
    if (!type || !id) { setDocs([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/linked-documents/${type}/${id}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      setDocs(res.ok && data.success ? (data.documents || []) : []);
    } catch (err) {
      // Non-fatal — the record is still shown; only the panel is missing.
      console.error('Failed to load linked documents:', err);
      setDocs([]);
    }
  }, [type, id]);

  // refreshKey: the host passes the record it renders, so each reload of the
  // host modal reloads the links with it.
  useEffect(() => { load(); }, [load, refreshKey]);

  if (docs.length === 0) return null;

  // Closing the opened document refreshes the modal underneath (onChange —
  // whose new record comes back here as refreshKey, reloading these chips) or,
  // with no onChange, just the chips: the user may have changed something.
  const open = (doc) => openEntity(doc.kind, doc.openId, {
    onClose: () => (onChange ? onChange() : load())
  });

  return (
    <div className="linked-documents">
      <h3>
        <span className="material-symbols-rounded">link</span>
        {title}
        <span className="linked-documents-count">{docs.length}</span>
      </h3>
      <div className="linked-doc-chips">
        {docs.map(doc => {
          const allocated = (doc.allocations || []).reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
          const against = [...new Set((doc.allocations || []).map(a => a.against).filter(Boolean))];
          const hasAllocation = (doc.allocations || []).length > 0;
          const shownAmount = hasAllocation ? allocated : doc.amount;
          const canOpen = available && doc.openId != null && doc.openId !== '';
          const tip = [
            `${canOpen ? 'Open' : ''} ${kindLabel(doc)} ${doc.number}`.trim(),
            doc.party,
            doc.haulageFor?.length && `Haulage for ${doc.haulageFor.join(', ')} — the PO's cost and status are not changed by it`,
            bookingText(doc) && `${bookingText(doc)}${doc.booking.note ? ` (${doc.booking.note})` : ''}`,
            doc.date && formatDateShort(doc.date),
            hasAllocation && doc.amount != null && `Total ${formatCurrency(doc.amount)}`,
            hasAllocation && `${formatCurrency(allocated)} allocated${against.length ? ` against ${against.join(', ')}` : ''}`
          ].filter(Boolean).join(' · ');
          return (
            <button
              key={`${doc.kind}-${doc.id}`}
              type="button"
              data-read-safe
              className={`linked-doc-chip kind-${doc.kind}${doc.poType === 'haulage' || doc.haulageLink || doc.haulageOf || doc.haulageFor?.length ? ' kind-haulage' : ''}${doc.adjustment ? ` adjustment-${doc.adjustment}` : ''}`}
              onClick={canOpen ? () => open(doc) : undefined}
              disabled={!canOpen}
              title={tip}
            >
              <span className="doc-type">{typeBadge(doc)}</span>
              <span className="doc-number">{doc.number}</span>
              {doc.status && <span className={`doc-status ${statusClass(doc)}`}>{statusText(doc)}</span>}
              {shownAmount != null && (
                <span className="doc-amount">
                  {formatCurrency(shownAmount)}{hasAllocation ? ' allocated' : ''}
                </span>
              )}
              {against.length > 0 && <span className="doc-against">→ {against.join(', ')}</span>}
              {doc.haulageFor?.length > 0 && <span className="doc-against">for {doc.haulageFor.join(', ')}</span>}
              {bookingText(doc) && <span className="doc-against">part {doc.booking.part} of {doc.booking.of}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default LinkedDocuments;
